/**
 * Bug Report Service
 *
 * Submits public bug/support reports to the BFF. Unlike every other service
 * in this app, this endpoint is AllowAny and tenant-agnostic: it must NOT
 * send the `domain` header, an Authorization header, or any tenant/session
 * cookie reference.
 */
import { API_ROUTES } from '../routes/api.tsx';
import { API_URL } from '../config/env.ts';

interface BugReportData {
  name: string;
  mail: string;
  description: string;
  telefono: string;
}

interface BugReportResponse {
  name: string;
  mail: string;
  description: string;
  telefono: string;
}

interface BackendErrorBody {
  error?: string;
  type?: string;
  code?: string;
}

/**
 * Parse a non-ok response from the backend, which uses the standard format:
 * { "error": "texto de error." }
 *
 * Throws an Error whose message is "Error <status>: <error>".
 */
async function throwBackendError(response: Response, fallback: string): Promise<never> {
  let body: BackendErrorBody = {};
  try {
    body = (await response.json()) as BackendErrorBody;
  } catch {
    // Response body is not JSON – keep empty object and use fallback
  }
  const message = body.error
    ? `Error ${response.status}: ${body.error}`
    : `Error ${response.status}: ${fallback}`;
  throw new Error(message);
}

/**
 * Submit a bug/support report.
 * No `domain` header, no Authorization header, no tenant context.
 */
export async function submitBugReport(data: BugReportData): Promise<BugReportResponse> {
  try {
    const response = await fetch(API_URL + API_ROUTES.SOPORTE.BUGS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Bug report submission failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Bug report submission error:', error);
    throw error;
  }
}

export type { BugReportData, BugReportResponse };
